import { useState, useEffect, useRef, useMemo } from "react"
import { useParams, useLocation } from "wouter"
import { useQuery, useMutation } from "@tanstack/react-query"
import { useForm, useFieldArray } from "react-hook-form"
import { toast } from "sonner"
import { ChevronLeft, ChevronRight, Plus, Trash2 } from "lucide-react"
import { Trans, useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { SpotGrid } from "@/components/SpotGrid"
import { PhotoGallery } from "@/components/PhotoGallery"
import { GuestFormFields } from "@/components/GuestFormFields"
import { AIGuidePanel } from "@/components/AIGuidePanel"
import { api, ApiRequestError } from "@/lib/api"
import { formatCurrency, formatDateRange, LOCALE_MAP } from "@/lib/utils"
import { inputClass } from "@/lib/styleClasses"
import { sanitizeRichText, isHtmlContent } from "@/lib/sanitize"
import { isTaxableAge } from "@fohet/caravan-booker-shared/utils/accommodation-tax"
import type {
    CreateReservationInput,
    ArealPublicDetail,
    AvailabilityResult,
    CreateReservationResponse,
    HoldResponse,
    SpotPhoto,
} from "@fohet/caravan-booker-shared/types"

type Step = 0 | 1 | 2 | 3

export const ReservationPage = () => {
    const { t, i18n } = useTranslation()
    const locale = LOCALE_MAP[i18n.language] ?? "en-US"
    const { slug } = useParams<{ slug: string }>()
    const [, navigate] = useLocation()
    const [step, setStep] = useState<Step>(0)
    const [availability, setAvailability] = useState<AvailabilityResult | null>(null)

    // Hold state
    const [holdId, setHoldId] = useState<string | null>(null)
    const [holdExpiresAt, setHoldExpiresAt] = useState<Date | null>(null)
    const [timeLeft, setTimeLeft] = useState<number>(0)

    // Keep a ref to holdId for unmount cleanup without stale closure issues
    const holdIdRef = useRef<string | null>(null)
    holdIdRef.current = holdId

    const STEP_LABELS = [
        t("guest.reservation.stepDates"),
        t("guest.reservation.stepSpot"),
        t("guest.reservation.stepGuests"),
        t("guest.reservation.stepConfirm"),
    ]

    const { data: areal, isLoading: arealLoading } = useQuery({
        queryKey: ["areal", slug, i18n.language],
        queryFn: () => api.get<ArealPublicDetail>(`/areals/public/${slug}?locale=${i18n.language}`),
    })

    const { data: spotPhotosMap } = useQuery({
        queryKey: ["spotPhotos", areal?.id],
        queryFn: () => api.get<Record<string, SpotPhoto[]>>(`/areals/${areal!.id}/spot-photos`),
        enabled: !!areal?.id && step === 1,
    })

    const spotsWithPhotos = useMemo(
        () => new Set(
            Object.keys(spotPhotosMap ?? {})
                .filter((k) => (spotPhotosMap?.[k]?.length ?? 0) > 0)
                .map(Number),
        ),
        [spotPhotosMap],
    )

    const today = new Date().toISOString().split("T")[0]!
    const tomorrow = new Date(Date.now() + 86400000).toISOString().split("T")[0]!

    const { register, handleSubmit, watch, control, setValue, getValues, trigger, formState: { errors } } = useForm<CreateReservationInput>({
        defaultValues: {
            arealId: "",
            arrivalDate: today,
            departureDate: tomorrow,
            guestCount: 1,
            guests: [{ firstName: "", lastName: "", dateOfBirth: "", nationality: "CZ", street: "", city: "", zip: "", country: "" }],
            licensePlate: "",
            paymentMethod: "bank_transfer",
            hasPet: false,
            email: "",
            locale: i18n.language === "cs" ? "cs" : i18n.language === "pl" ? "pl" : "en",
            // acceptedPrivacy is z.literal(true) server-side. We can't put
            // `false` here (TS rejects it against the literal type), and
            // `true` as a default would auto-accept consent on page load
            // — both wrong. We rely on RHF's required-checkbox rule to
            // block submission and on the server to reject any payload
            // missing the explicit true.
        },
    })

    const { fields: guestFields, append, remove } = useFieldArray({ control, name: "guests" })

    // Countdown timer effect
    useEffect(() => {
        if (!holdExpiresAt) {
            setTimeLeft(0)
            return
        }
        const tick = () => {
            const remaining = Math.max(0, Math.floor((holdExpiresAt.getTime() - Date.now()) / 1000))
            setTimeLeft(remaining)
            if (remaining === 0) {
                setHoldId(null)
                setHoldExpiresAt(null)
                setValue("spotNumber", undefined)
                toast.error(t("guest.reservation.holdExpired"))
                setStep(1)
            }
        }
        tick()
        const interval = setInterval(tick, 1000)
        return () => clearInterval(interval)
    }, [holdExpiresAt, setValue, t])

    // Cleanup hold on unmount
    useEffect(() => {
        return () => {
            const id = holdIdRef.current
            if (id) {
                api.delete(`/reservations/hold/${id}`).catch(() => {})
            }
        }
    }, [])

    // Set arrival/departure time defaults from areal check-in/check-out times
    useEffect(() => {
        if (!areal) return
        if (areal.checkInTime) {
            setValue("arrivalTime", areal.checkInTime)
        }
        if (areal.checkOutTime) {
            setValue("departureTime", areal.checkOutTime)
        }
    }, [areal, setValue])

    const releaseHold = async (id: string): Promise<void> => {
        try {
            await api.delete(`/reservations/hold/${id}`)
        } catch {
            // fire-and-forget — ignore errors
        }
        setHoldId(null)
        setHoldExpiresAt(null)
        setTimeLeft(0)
    }

    const checkAvailabilityMutation = useMutation({
        mutationFn: (data: { arealId: string; arrivalDate: string; departureDate: string }) =>
            api.post<AvailabilityResult>("/reservations/check-availability", data),
        onSuccess: (data) => {
            setAvailability(data)
            setStep(1)
        },
        onError: () => {
            toast.error(t("guest.reservation.toastCheckAvailFailed"))
        },
    })

    const createHoldMutation = useMutation({
        mutationFn: (data: { arealId: string; spotNumber: number; arrivalDate: string; departureDate: string }) =>
            api.post<HoldResponse>("/reservations/hold", data),
        onSuccess: (data) => {
            setHoldId(data.holdId)
            setHoldExpiresAt(new Date(data.expiresAt))
            setStep(2)
        },
        onError: (e) => {
            if (e instanceof ApiRequestError && e.status === 409) {
                toast.error(t("guest.reservation.holdTaken"))
            } else {
                toast.error(t("guest.reservation.holdError"))
            }
        },
    })

    const createReservationMutation = useMutation({
        mutationFn: (data: CreateReservationInput) =>
            api.post<CreateReservationResponse>("/reservations", data),
        onSuccess: (data) => {
            setHoldId(null)
            setHoldExpiresAt(null)
            toast.success(t("guest.reservation.toastCreated"))
            navigate(`~/r/${data.magicToken}`)
        },
        onError: (e) => {
            const code = e instanceof ApiRequestError ? e.code : undefined
            toast.error(code ? t(`errors.${code}`, { defaultValue: t("guest.reservation.toastCreateFailed") }) : t("guest.reservation.toastCreateFailed"))
        },
    })

    if (arealLoading) {
        return (
            <div className="flex items-center justify-center py-24">
                <div className="text-slate-400 font-bold">{t("guest.reservation.loading")}</div>
            </div>
        )
    }

    if (!areal) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-red-700 font-bold">
                {t("guest.reservation.notFound")}
            </div>
        )
    }

    const arrivalDate = watch("arrivalDate")
    const departureDate = watch("departureDate")
    const arrivalTime = watch("arrivalTime")
    const departureTime = watch("departureTime")
    const selectedSpot = watch("spotNumber")

    // datetime-local value = "YYYY-MM-DDTHH:MM"
    const arrivalDatetime = arrivalDate && arrivalTime ? `${arrivalDate}T${arrivalTime}` : arrivalDate ?? ""
    const departureDatetime = departureDate && departureTime ? `${departureDate}T${departureTime}` : departureDate ?? ""

    const handleArrivalDatetimeChange = (value: string) => {
        const [date, time] = value.split("T")
        if (date) setValue("arrivalDate", date)
        if (time) setValue("arrivalTime", time)
    }

    const handleDepartureDatetimeChange = (value: string) => {
        const [date, time] = value.split("T")
        if (date) setValue("departureDate", date)
        if (time) setValue("departureTime", time)
    }
    const guestCount = watch("guestCount")
    const hasPet = watch("hasPet")
    const guests = watch("guests")

    const priceList = areal.priceLists[0]
    const nights = arrivalDate && departureDate
        ? Math.max(0, Math.round((new Date(departureDate).getTime() - new Date(arrivalDate).getTime()) / 86400000))
        : 0

    // Count guests who are NOT exempt from local tourist tax. Rule lives
    // in the shared package so server and client agree — see comment in
    // accommodation-tax.ts. Currently only <18 is exempt.
    const taxableGuestCount = (() => {
        const ref = arrivalDate || new Date().toISOString().slice(0, 10)
        const filled = (guests ?? []).filter((g) => g.dateOfBirth && /^\d{4}-\d{2}-\d{2}$/.test(g.dateOfBirth))
        if (filled.length === 0) return guestCount ?? 1
        return filled.filter((g) => isTaxableAge(g.dateOfBirth, ref)).length
    })()

    const estimatedPrice = priceList && nights > 0
        ? (priceList.pricePerNight * nights)
            + (priceList.localTaxPerPersonPerNight * taxableGuestCount * nights)
            + (hasPet ? priceList.petFeePerNight * nights : 0)
        : null

    const handleDateStep = () => {
        const arrival = getValues("arrivalDate")
        const departure = getValues("departureDate")
        const aTime = getValues("arrivalTime")
        const dTime = getValues("departureTime")
        if (!arrival || !aTime) {
            toast.error(t("guest.reservation.toastSelectArrivalTime"))
            return
        }
        if (!departure || !dTime) {
            toast.error(t("guest.reservation.toastSelectDepartureTime"))
            return
        }
        if (departure <= arrival) {
            toast.error(t("guest.reservation.toastDepartureMustBeAfter"))
            return
        }
        if (!areal) return
        // If we already have a hold, release it before re-checking dates
        if (holdId) {
            releaseHold(holdId).catch(() => {})
            setValue("spotNumber", undefined)
        }
        setValue("arealId", areal.id)
        checkAvailabilityMutation.mutate({ arealId: areal.id, arrivalDate: arrival, departureDate: departure })
    }

    const handleSpotStep = async () => {
        if (!selectedSpot) {
            toast.error(t("guest.reservation.toastSelectSpot"))
            return
        }
        if (!areal) return

        // If the user changed their spot selection while a hold exists, release the old one first
        if (holdId) {
            await releaseHold(holdId)
        }

        createHoldMutation.mutate({
            arealId: areal.id,
            spotNumber: selectedSpot,
            arrivalDate: getValues("arrivalDate"),
            departureDate: getValues("departureDate"),
        })
    }

    const handleGuestStep = async () => {
        const guests = getValues("guests")
        if (!guests || guests.length === 0) {
            toast.error(t("guest.reservation.toastAddGuest"))
            return
        }

        const guestFieldNames = guests.flatMap((_, i) => [
            `guests.${i}.firstName`,
            `guests.${i}.lastName`,
            `guests.${i}.dateOfBirth`,
            `guests.${i}.nationality`,
            `guests.${i}.documentNumber`,
            `guests.${i}.street`,
            `guests.${i}.city`,
            `guests.${i}.zip`,
            `guests.${i}.country`,
            `guests.${i}.purposeOfStay`,
        ] as const)

        const valid = await trigger([
            "email",
            "phone",
            "licensePlate",
            ...guestFieldNames,
        ])
        if (!valid) return

        setValue("guestCount", guests.length)
        setStep(3)
    }

    const handleBackToStep0 = () => {
        if (holdId) {
            releaseHold(holdId).catch(() => {})
            setValue("spotNumber", undefined)
        }
        setStep(0)
    }

    const handleBackToStep1 = () => {
        setStep(1)
    }

    const handleBackToStep2 = () => {
        setStep(2)
    }

    const onSubmit = handleSubmit((data) => {
        const submitData: CreateReservationInput = {
            ...data,
            guestCount: data.guests.length,
            ...(holdId ? { holdId } : {}),
        }
        createReservationMutation.mutate(submitData)
    })

    const formatTimeLeft = (seconds: number): string => {
        const m = Math.floor(seconds / 60)
        const s = seconds % 60
        return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
    }

    return (
        <div className="max-w-2xl mx-auto pb-4 sm:pb-0">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-3xl font-black tracking-tight text-slate-900">{areal.name}</h1>
                {areal.description && (
                    isHtmlContent(areal.description)
                        ? <div className="text-slate-500 font-bold mt-2 rich-text" dangerouslySetInnerHTML={{ __html: sanitizeRichText(areal.description) }} />
                        : <p className="text-slate-500 font-bold mt-2 whitespace-pre-line">{areal.description}</p>
                )}
            </div>

            {/* Step indicator */}
            <div className="flex items-center gap-2 mb-8">
                {STEP_LABELS.map((label, i) => {
                    const backHandlers = [handleBackToStep0, handleBackToStep1, handleBackToStep2, null]
                    const isClickable = i < step
                    const handler = backHandlers[i]
                    return (
                        <div key={label} className="flex items-center">
                            <button
                                type="button"
                                disabled={!isClickable}
                                onClick={isClickable && handler ? handler : undefined}
                                className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-black transition-all duration-200 ${
                                    isClickable ? "bg-brand text-white cursor-pointer hover:bg-brand-dark active:scale-95" :
                                    i === step ? "bg-charcoal text-white cursor-default" :
                                    "bg-slate-100 text-slate-400 cursor-default"
                                }`}
                            >
                                {i < step ? "✓" : i + 1}
                            </button>
                            <span className={`ml-1.5 text-sm font-black hidden sm:block ${i === step ? "text-slate-900" : isClickable ? "text-brand cursor-pointer" : "text-slate-400"}`}
                                onClick={isClickable && handler ? handler : undefined}
                            >
                                {label}
                            </span>
                            {i < STEP_LABELS.length - 1 && (
                                <div className={`w-8 h-0.5 ml-2 ${i < step ? "bg-brand" : "bg-slate-200"}`} />
                            )}
                        </div>
                    )
                })}
            </div>

            {/* Hold countdown banner */}
            {holdId && step >= 1 && timeLeft > 0 && (
                <div className="bg-amber-50 border border-amber-200 text-amber-700 rounded-2xl px-4 py-2 text-sm font-bold mb-4 flex items-center justify-between">
                    <span>
                        {t("guest.reservation.holdActive", {
                            spot: selectedSpot ?? "–",
                            time: formatTimeLeft(timeLeft),
                        })}
                    </span>
                </div>
            )}

            <form
                onSubmit={onSubmit}
                onKeyDown={(e) => { if (e.key === "Enter" && step < 3) e.preventDefault() }}
            >
                {/* Step 0: Dates */}
                {step === 0 && (
                    <div className="bg-white rounded-[40px] border border-slate-100 shadow-xl p-5 sm:p-8 space-y-5">
                        <h2 className="text-xl font-black text-slate-900">{t("guest.reservation.selectDates")}</h2>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs font-black uppercase tracking-widest text-slate-600 mb-1.5 block">
                                    {t("guest.reservation.arrival")}
                                </label>
                                <Input
                                    type="datetime-local"
                                    min={`${today}T00:00`}
                                    className={inputClass}
                                    value={arrivalDatetime}
                                    onChange={(e) => handleArrivalDatetimeChange(e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="text-xs font-black uppercase tracking-widest text-slate-600 mb-1.5 block">
                                    {t("guest.reservation.departure")}
                                </label>
                                <Input
                                    type="datetime-local"
                                    min={arrivalDatetime || `${today}T00:00`}
                                    className={inputClass}
                                    value={departureDatetime}
                                    onChange={(e) => handleDepartureDatetimeChange(e.target.value)}
                                />
                            </div>
                        </div>

                        {areal.checkInTime && (
                            <p className="text-sm text-slate-500 font-bold">
                                {t("guest.reservation.checkInOut", { checkIn: areal.checkInTime, checkOut: areal.checkOutTime })}
                            </p>
                        )}

                        {estimatedPrice !== null && nights > 0 && (
                            <div className="bg-charcoal text-white rounded-[28px] p-5">
                                <p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-1">
                                    {t("guest.reservation.estimatedTotal", { count: nights })}
                                </p>
                                <p className="text-2xl font-black">{formatCurrency(estimatedPrice, areal.currency, locale)}</p>
                            </div>
                        )}

                        <Button
                            type="button"
                            onClick={handleDateStep}
                            disabled={checkAvailabilityMutation.isPending}
                            className="hidden sm:flex w-full bg-brand text-white font-black rounded-[28px] shadow-xl hover:bg-brand-dark active:scale-95 transition-all duration-200 py-4"
                        >
                            {checkAvailabilityMutation.isPending ? t("guest.reservation.checkingAvailability") : t("guest.reservation.checkAvailability")}
                            <ChevronRight className="size-4" />
                        </Button>
                    </div>
                )}

                {/* Step 1: Spot selection */}
                {step === 1 && availability && (
                    <div className="bg-white rounded-[40px] border border-slate-100 shadow-xl p-5 sm:p-8 space-y-5">
                        <div>
                            <h2 className="text-xl font-black text-slate-900 mb-1">{t("guest.reservation.chooseSpot")}</h2>
                            <p className="text-sm font-bold text-slate-500">
                                {t("guest.reservation.spotsAvailable", {
                                    available: availability.available.length,
                                    total: availability.totalSpots,
                                    dateRange: formatDateRange(arrivalDate, departureDate, locale),
                                })}
                            </p>
                        </div>

                        <SpotGrid
                            total={availability.totalSpots}
                            occupied={availability.occupied}
                            selected={selectedSpot ?? null}
                            onSelect={(n) => setValue("spotNumber", n)}
                            hasPhotos={spotsWithPhotos}
                        />

                        {/* Spot photo strip — shown when the selected spot has photos */}
                        {selectedSpot !== null && (spotPhotosMap?.[String(selectedSpot)]?.length ?? 0) > 0 && (
                            <div>
                                <p className="text-xs font-black uppercase tracking-widest text-slate-500 mb-2">
                                    {t("guest.reservation.spotPhotos", { number: selectedSpot })}
                                </p>
                                <PhotoGallery
                                    photos={(spotPhotosMap?.[String(selectedSpot)] ?? []).map((p) => ({ url: p.url, altText: p.altText }))}
                                    thumbWidth="w-40"
                                    thumbHeight="h-28"
                                />
                            </div>
                        )}

                        <div className="hidden sm:flex gap-3">
                            <Button type="button" onClick={handleBackToStep0} className="bg-slate-100 text-slate-600 font-bold rounded-2xl hover:bg-slate-200 active:scale-95 transition-all duration-200 px-5">
                                <ChevronLeft className="size-4" /> {t("common.back")}
                            </Button>
                            <Button type="button" onClick={handleSpotStep} disabled={createHoldMutation.isPending} className="flex-1 bg-brand text-white font-black rounded-[28px] shadow-xl hover:bg-brand-dark active:scale-95 transition-all duration-200 py-4">
                                {createHoldMutation.isPending ? t("guest.reservation.checkingAvailability") : t("common.continue")} <ChevronRight className="size-4" />
                            </Button>
                        </div>
                    </div>
                )}

                {/* Step 2: Guests */}
                {step === 2 && (
                    <div className="bg-white rounded-[40px] border border-slate-100 shadow-xl p-5 sm:p-8 space-y-5">
                        <h2 className="text-xl font-black text-slate-900">{t("guest.reservation.guestInfo")}</h2>

                        <div>
                            <label className="text-xs font-black uppercase tracking-widest text-slate-600 mb-1.5 block">
                                {t("guest.reservation.communicationLanguage")}
                            </label>
                            <select className={inputClass} {...register("locale")}>
                                <option value="cs">{t("guest.reservation.languageCs")}</option>
                                <option value="pl">{t("guest.reservation.languagePl")}</option>
                                <option value="en">{t("guest.reservation.languageEn")}</option>
                            </select>
                            <p className="text-xs text-slate-500 mt-1.5">
                                {t("guest.reservation.communicationLanguageHint")}
                            </p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs font-black uppercase tracking-widest text-slate-600 mb-1.5 block">
                                    {t("guest.reservation.email")}
                                </label>
                                <Input
                                    type="email"
                                    placeholder={t("guest.reservation.emailPlaceholder")}
                                    className={inputClass}
                                    error={errors.email?.message}
                                    {...register("email", {
                                        required: t("guest.reservation.emailRequired"),
                                        pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: t("guest.reservation.emailInvalid") },
                                    })}
                                />
                            </div>
                            <div>
                                <label className="text-xs font-black uppercase tracking-widest text-slate-600 mb-1.5 block">
                                    {t("guest.reservation.phone")}
                                </label>
                                <Input
                                    type="tel"
                                    placeholder={t("guest.reservation.phonePlaceholder")}
                                    className={inputClass}
                                    error={errors.phone?.message}
                                    {...register("phone", {
                                        required: t("guest.reservation.phoneRequired"),
                                        pattern: { value: /^\+?[0-9\s\-().]{7,20}$/, message: t("guest.reservation.phoneInvalid") },
                                    })}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs font-black uppercase tracking-widest text-slate-600 mb-1.5 block">
                                    {t("guest.reservation.licensePlate")}
                                </label>
                                <Input
                                    placeholder={t("guest.reservation.licensePlatePlaceholder")}
                                    className={`${inputClass} uppercase`}
                                    error={errors.licensePlate?.message}
                                    {...register("licensePlate", {
                                        required: t("guest.reservation.licensePlateRequired"),
                                        maxLength: { value: 20, message: t("guest.reservation.licensePlateMax") },
                                    })}
                                />
                            </div>
                            <div>
                                <label className="text-xs font-black uppercase tracking-widest text-slate-600 mb-1.5 block">
                                    {t("guest.reservation.vehicleType")}
                                </label>
                                <Input
                                    placeholder={t("guest.reservation.vehicleTypePlaceholder")}
                                    className={inputClass}
                                    {...register("vehicleType")}
                                />
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <input
                                type="checkbox"
                                id="hasPet"
                                className="w-5 h-5 accent-brand cursor-pointer"
                                {...register("hasPet")}
                            />
                            <label htmlFor="hasPet" className="font-bold text-slate-700 cursor-pointer">
                                {t("guest.reservation.hasPet")}
                            </label>
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <p className="text-xs font-black uppercase tracking-widest text-slate-600">{t("guest.reservation.guests")}</p>
                                <button
                                    type="button"
                                    onClick={() => append({ firstName: "", lastName: "", dateOfBirth: "", nationality: "CZ", street: "", city: "", zip: "", country: "" })}
                                    className="flex items-center gap-1 text-xs font-black text-brand hover:text-brand-dark transition-colors"
                                >
                                    <Plus className="size-3.5" /> {t("guest.reservation.addGuest")}
                                </button>
                            </div>
                            <div className="space-y-3">
                                {guestFields.map((field, i) => (
                                    <div key={field.id} className="relative">
                                        <GuestFormFields
                                            index={i}
                                            register={register}
                                            errors={errors}
                                            control={control}
                                            setValue={setValue}
                                            getValues={getValues}
                                        />
                                        {guestFields.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => remove(i)}
                                                className="absolute top-4 right-4 text-red-400 hover:text-red-600 transition-colors"
                                            >
                                                <Trash2 className="size-4" />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <div className="mt-3 flex justify-center">
                                <button
                                    type="button"
                                    onClick={() => append({ firstName: "", lastName: "", dateOfBirth: "", nationality: "CZ", street: "", city: "", zip: "", country: "" })}
                                    className="flex items-center gap-2 text-sm font-black text-brand hover:text-brand-dark transition-colors px-4 py-2 rounded-2xl border-2 border-dashed border-brand/40 hover:border-brand"
                                >
                                    <Plus className="size-4" /> {t("guest.reservation.addGuest")}
                                </button>
                            </div>
                        </div>

                        <div>
                            <label className="text-xs font-black uppercase tracking-widest text-slate-600 mb-1.5 block">
                                {t("guest.reservation.referralSource")}
                            </label>
                            <Input
                                placeholder={t("guest.reservation.referralSourcePlaceholder")}
                                className={inputClass}
                                maxLength={500}
                                {...register("referralSource")}
                            />
                        </div>

                        <div className="hidden sm:flex gap-3">
                            <Button type="button" onClick={handleBackToStep1} className="bg-slate-100 text-slate-600 font-bold rounded-2xl hover:bg-slate-200 active:scale-95 transition-all duration-200 px-5">
                                <ChevronLeft className="size-4" /> {t("common.back")}
                            </Button>
                            <Button type="button" onClick={handleGuestStep} className="flex-1 bg-brand text-white font-black rounded-[28px] shadow-xl hover:bg-brand-dark active:scale-95 transition-all duration-200 py-4">
                                {t("guest.reservation.review")} <ChevronRight className="size-4" />
                            </Button>
                        </div>
                    </div>
                )}

                {/* Step 3: Review & Confirm */}
                {step === 3 && (
                    <div className="space-y-4">
                        {/* Clear heading so it's obvious this is the final step */}
                        <div className="text-center">
                            <p className="text-xs font-black uppercase tracking-widest text-brand mb-1">{t("guest.reservation.finalStep")}</p>
                            <h2 className="text-2xl font-black text-slate-900">{t("guest.reservation.reviewHeading")}</h2>
                            <p className="text-slate-500 font-bold text-sm mt-1">{t("guest.reservation.reviewSubtitle")}</p>
                        </div>

                        {/* Stay summary */}
                        <div className="bg-charcoal text-white rounded-[40px] shadow-2xl p-5 sm:p-8">
                            <p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-5">
                                {t("guest.reservation.summaryTitle")}
                            </p>
                            <div className="space-y-3">
                                <div className="flex justify-between">
                                    <span className="font-bold text-slate-300">{t("guest.reservation.summaryCampground")}</span>
                                    <span className="font-black">{areal.name}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="font-bold text-slate-300">{t("guest.reservation.summaryDates")}</span>
                                    <span className="font-black">{formatDateRange(arrivalDate, departureDate, locale)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="font-bold text-slate-300">{t("guest.reservation.summarySpot")}</span>
                                    <span className="font-black">#{selectedSpot}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="font-bold text-slate-300">{t("guest.reservation.summaryGuests")}</span>
                                    <span className="font-black">{guestFields.length}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="font-bold text-slate-300">{t("guest.reservation.summaryVehicle")}</span>
                                    <span className="font-black">{getValues("licensePlate")}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="font-bold text-slate-300">{t("guest.reservation.summaryContact")}</span>
                                    <span className="font-black">{getValues("email")}</span>
                                </div>
                                {estimatedPrice !== null && (
                                    <div className="flex justify-between pt-4 border-t border-slate-700">
                                        <span className="font-bold text-slate-300">{t("guest.reservation.summaryTotal")}</span>
                                        <span className="text-2xl font-black text-brand">
                                            {formatCurrency(estimatedPrice, areal.currency, locale)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Guest list */}
                        <div className="bg-white rounded-[40px] border border-slate-100 shadow-xl p-5 sm:p-8 space-y-3">
                            <h3 className="font-black text-slate-900 mb-1">{t("guest.reservation.registeredGuests")}</h3>
                            {guestFields.map((field, i) => {
                                const g = getValues(`guests.${i}`)
                                return (
                                    <div key={field.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                                        <span className="font-bold text-slate-800">{g?.firstName} {g?.lastName}</span>
                                        <span className="text-xs font-bold text-slate-400 uppercase">{g?.nationality}</span>
                                    </div>
                                )
                            })}
                        </div>

                        {/* Payment method */}
                        <div className="bg-white rounded-[40px] border border-slate-100 shadow-xl p-5 sm:p-8 space-y-4">
                            <h3 className="font-black text-slate-900">{t("guest.reservation.paymentMethod")}</h3>
                            <div className="space-y-2">
                                {([
                                    { method: "bank_transfer", label: "bankTransfer", desc: "bankTransferDesc" },
                                    { method: "qr_payment", label: "qrPayment", desc: "qrPaymentDesc" },
                                    { method: "cash", label: "cash", desc: "cashDesc" },
                                ] as const).map(({ method, label, desc }) => (
                                    <label key={method} className="flex items-center gap-3 p-4 rounded-2xl border border-slate-200 cursor-pointer hover:border-brand transition-colors has-[:checked]:border-brand has-[:checked]:bg-brand/5">
                                        <input
                                            type="radio"
                                            value={method}
                                            className="accent-brand"
                                            {...register("paymentMethod")}
                                        />
                                        <div>
                                            <p className="font-black text-slate-800">
                                                {t(`guest.reservation.${label}`)}
                                            </p>
                                            <p className="text-xs font-bold text-slate-400">
                                                {t(`guest.reservation.${desc}`)}
                                            </p>
                                        </div>
                                    </label>
                                ))}
                            </div>

                            {/* GDPR consent — required to submit. Server validates the
                                acceptedPrivacy literal(true) and records the current
                                Privacy Policy version on the reservation row. */}
                            <label className="flex items-start gap-2 pt-2 text-sm font-bold text-slate-700 cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="mt-1 size-4 shrink-0 accent-brand"
                                    {...register("acceptedPrivacy", { required: t("guest.reservation.acceptPrivacyRequired") })}
                                />
                                <span>
                                    <Trans
                                        i18nKey="guest.reservation.acceptPrivacy"
                                        components={{
                                            privacy: <a href="/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-brand hover:text-brand-dark underline" />,
                                        }}
                                    />
                                </span>
                            </label>
                            {errors.acceptedPrivacy && (
                                <p className="text-xs text-red-500 font-bold ml-6">{errors.acceptedPrivacy.message as string}</p>
                            )}

                            {/* Final notice */}
                            <p className="text-xs font-bold text-slate-400 text-center pt-1">
                                {t("guest.reservation.confirmationEmailNotice", { email: getValues("email") })}
                            </p>

                            <div className="hidden sm:flex gap-3 pt-2">
                                <Button type="button" onClick={handleBackToStep2} className="bg-slate-100 text-slate-600 font-bold rounded-2xl hover:bg-slate-200 active:scale-95 transition-all duration-200 px-5">
                                    <ChevronLeft className="size-4" /> {t("common.back")}
                                </Button>
                                <Button type="submit" disabled={createReservationMutation.isPending} className="flex-1 bg-brand text-white font-black rounded-[28px] shadow-xl hover:bg-brand-dark active:scale-95 transition-all duration-200 py-4 text-base">
                                    {createReservationMutation.isPending ? t("guest.reservation.booking") : t("guest.reservation.confirmBook")}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            {/* Fixed bottom action bar — mobile only */}
            <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur border-t border-slate-100 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] z-20">
                <div className="max-w-2xl mx-auto flex gap-3">
                    {step > 0 && (
                        <Button
                            type="button"
                            onClick={step === 1 ? handleBackToStep0 : step === 2 ? handleBackToStep1 : handleBackToStep2}
                            className="bg-slate-100 text-slate-600 font-bold rounded-2xl hover:bg-slate-200 active:scale-95 transition-all duration-200 px-5"
                        >
                            <ChevronLeft className="size-4" />
                        </Button>
                    )}
                    {step === 0 && (
                        <Button
                            type="button"
                            onClick={handleDateStep}
                            disabled={checkAvailabilityMutation.isPending}
                            className="flex-1 bg-brand text-white font-black rounded-[28px] shadow-xl hover:bg-brand-dark active:scale-95 transition-all duration-200 py-4"
                        >
                            {checkAvailabilityMutation.isPending ? t("guest.reservation.checkingAvailability") : t("guest.reservation.checkAvailability")}
                            <ChevronRight className="size-4" />
                        </Button>
                    )}
                    {step === 1 && (
                        <Button
                            type="button"
                            onClick={handleSpotStep}
                            disabled={createHoldMutation.isPending}
                            className="flex-1 bg-brand text-white font-black rounded-[28px] shadow-xl hover:bg-brand-dark active:scale-95 transition-all duration-200 py-4"
                        >
                            {createHoldMutation.isPending ? t("guest.reservation.checkingAvailability") : t("common.continue")}
                            <ChevronRight className="size-4" />
                        </Button>
                    )}
                    {step === 2 && (
                        <Button
                            type="button"
                            onClick={handleGuestStep}
                            className="flex-1 bg-brand text-white font-black rounded-[28px] shadow-xl hover:bg-brand-dark active:scale-95 transition-all duration-200 py-4"
                        >
                            {t("guest.reservation.review")}
                            <ChevronRight className="size-4" />
                        </Button>
                    )}
                    {step === 3 && (
                        <Button
                            type="submit"
                            disabled={createReservationMutation.isPending}
                            className="flex-1 bg-brand text-white font-black rounded-[28px] shadow-xl hover:bg-brand-dark active:scale-95 transition-all duration-200 py-4 text-base"
                        >
                            {createReservationMutation.isPending ? t("guest.reservation.booking") : t("guest.reservation.confirmBook")}
                        </Button>
                    )}
                </div>
            </div>
            </form>

            <AIGuidePanel arealId={areal.id} arealName={areal.name} />
        </div>
    )
}
